/**
 * The value input of a dated scalar's change form (1.Y deliverable 2): 1.B-2's editor for the
 * field's kind family (`types/pills/editors/`), writing into the form instead of the page. The
 * fields dialog passes it as `renderValueInput`.
 */
import type { FieldDefV1 } from '@codex/contracts';
import { toPropValue } from '../types/model/values.js';
import {
  BooleanEditor,
  MultiSelectEditor,
  SelectEditor,
} from '../types/pills/editors/ChoiceEditors.js';
import type { EditorProps } from '../types/pills/editors/shared.js';
import { LongTextEditor, TextEditor } from '../types/pills/editors/TextEditors.js';
import type { RenderValueInput } from './dated-field-editor.js';

export function valueInputFor(field: FieldDefV1): RenderValueInput {
  return function ValueInput({ value, onChange }) {
    const common: EditorProps = {
      field,
      value: value === null ? undefined : toPropValue(field, value),
      error: null,
      commit: onChange,
    };
    if (field.kind === 'boolean') return <BooleanEditor {...common} />;
    if (field.kind === 'select') return <SelectEditor {...common} />;
    if (field.kind === 'multi_select') return <MultiSelectEditor {...common} />;
    // The typed text is the draft over an empty stored value, so a blur keeps it.
    const text = {
      ...common,
      value: undefined,
      draft: typeof value === 'string' ? value : undefined,
      setDraft: (t: string | undefined) => onChange(t ?? ''),
    };
    return field.kind === 'long_text' ? <LongTextEditor {...text} /> : <TextEditor {...text} />;
  };
}
